import React from "react";

// ============================================================
// TextStyleControls — Phase (issue 5) shared editor primitive.
// ============================================================
// Two rows of pill toggles:
//   • Text Color — White / Black / Auto (colour swatches)
//   • Text Effect — None / Shadow / Stroke / Both (pill toggles)
//
// Persists as textColor + textEffect fields alongside coverImage,
// zoom, position, and overlay on the parent record.
// ============================================================

const COLOR_OPTIONS = [
  { value: "white", label: "White", swatch: "#ffffff", border: "rgba(0,0,0,0.15)" },
  { value: "black", label: "Black", swatch: "#111111", border: "rgba(255,255,255,0.15)" },
  { value: "auto",  label: "Auto",  swatch: "linear-gradient(90deg,#ffffff 0 50%,#111111 50% 100%)", border: "var(--kc-border)" },
];

const EFFECT_OPTIONS = [
  { value: "none",   label: "None"   },
  { value: "shadow", label: "Shadow" },
  { value: "stroke", label: "Stroke" },
  { value: "both",   label: "Both"   },
];

export function TextStyleControls({
  textColor = "white",
  textEffect = "shadow",
  onColor,
  onEffect,
  overlay = 55,
  onOverlay,
  previewImage = "",
  previewTitle = "Sample Title",
  previewSubtitle = "Sample subtitle",
}) {
  const previewOverlay = overlay;
  // Resolve preview colour + effect exactly the way <CoverImage> does — so
  // Super Admin sees the real output as they toggle.
  const effColor = textColor === "auto"
    ? (previewOverlay >= 40 ? "#ffffff" : "#111111")
    : (textColor === "black" ? "#111111" : "#ffffff");
  const isWhite = effColor === "#ffffff";
  const useShadow = textEffect === "shadow" || textEffect === "both";
  const useStroke = textEffect === "stroke" || textEffect === "both";
  const textShadow = useShadow ? "0 2px 4px rgba(0,0,0,0.8)" : "none";
  const stroke = useStroke
    ? (isWhite ? "1px rgba(0,0,0,0.6)" : "1px rgba(255,255,255,0.6)")
    : "0";
  return (
    <div
      data-testid="text-style-controls"
      style={{
        marginTop: "var(--space-sm)",
        padding: "var(--space-sm)",
        background: "var(--kc-elevated)",
        border: "1px solid var(--kc-border)",
        borderRadius: "var(--kc-radius-sm)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-sm)",
      }}
    >
      <div className="text-eyebrow">Text Style</div>

      {/* Live 16:9 preview — Iter 79 improvement. Reflects the current
          colour + effect + overlay choices without closing the modal. */}
      <div
        data-testid="text-style-preview"
        style={{
          position: "relative",
          aspectRatio: "16 / 9",
          width: "100%",
          maxWidth: 260,
          borderRadius: "var(--kc-radius-xs)",
          overflow: "hidden",
          background: previewImage
            ? `url("${previewImage}") center/cover no-repeat`
            : "linear-gradient(135deg, var(--kc-accent) 0%, var(--kc-elevated) 100%)",
          border: "1px solid var(--kc-border)",
        }}
      >
        {/* Dark overlay proportional to overlay percentage */}
        <div style={{
          position: "absolute", inset: 0,
          background: `rgba(0,0,0,${Math.max(0, Math.min(100, previewOverlay)) / 100})`,
        }} />
        <div style={{
          position: "absolute", left: "var(--space-sm)", right: "var(--space-sm)",
          bottom: "var(--space-xs)",
          color: effColor,
        }}>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            textShadow,
            WebkitTextStroke: stroke,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{previewTitle}</div>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 10,
            fontWeight: 500,
            marginTop: 1,
            textShadow,
            WebkitTextStroke: stroke,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{previewSubtitle}</div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", flexWrap: "wrap" }}>
        <span className="text-meta" style={{ minWidth: 44 }}>Colour</span>
        {COLOR_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            data-testid={`text-color-${opt.value}`}
            aria-pressed={textColor === opt.value}
            onClick={() => onColor && onColor(opt.value)}
            title={opt.label}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              border: textColor === opt.value ? "2px solid var(--kc-accent)" : `1.5px solid ${opt.border}`,
              background: opt.swatch,
              cursor: "pointer",
              padding: 0,
              boxShadow: textColor === opt.value ? "0 0 0 2px var(--kc-accent-bg)" : "none",
              transition: "box-shadow 0.15s ease, border-color 0.15s ease",
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-xs)", flexWrap: "wrap" }}>
        <span className="text-meta" style={{ minWidth: 44 }}>Effect</span>
        {EFFECT_OPTIONS.map(opt => {
          const active = textEffect === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`text-effect-${opt.value}`}
              aria-pressed={active}
              onClick={() => onEffect && onEffect(opt.value)}
              style={{
                padding: "6px 12px",
                borderRadius: "999px",
                border: active ? "1.5px solid var(--kc-accent)" : "1.5px solid var(--kc-border)",
                background: active ? "var(--kc-accent-bg)" : "transparent",
                color: active ? "var(--kc-accent)" : "var(--kc-sub)",
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Overlay opacity slider — live-updates the preview so admins can
          tune contrast without leaving the modal. */}
      {onOverlay && (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", flexWrap: "wrap" }}>
          <span className="text-meta" style={{ minWidth: 44 }}>Overlay</span>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={overlay}
            onChange={(e) => onOverlay(parseInt(e.target.value, 10) || 0)}
            data-testid="text-overlay-slider"
            style={{ flex: 1, minWidth: 120, accentColor: "var(--kc-accent)" }}
          />
          <span className="text-meta" data-testid="text-overlay-value" style={{ minWidth: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
            {overlay}%
          </span>
        </div>
      )}
    </div>
  );
}

export default TextStyleControls;
